import * as React from 'react';
import styled from 'styled-components';
import Headline from '@adecco/base-app/src/components/atoms/Headline/Headline';
import { moreThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import ProcessItem, {
  IProcessItem,
} from '@adecco/base-app/src/components/atoms/ProcessItem/ProcessItem';
import calcrem from '@adecco/base-app/src/utilities/calcrem/calcrem';
import Container from '@adecco/base-app/src/components/atoms/Container/Container';
import dynamic from 'next/dynamic';
import { useisMobile } from '@adecco/base-app/src/hooks/useIsMobile';

const Carousel = dynamic(() => import('@adecco/base-app/src/components/atoms/Carousel/Carousel'));

export interface IProcess {
  title: string;
  subline?: string;
  items: ReadonlyArray<IProcessItem>;
  centeredHeadline?: boolean;
  withNumbers?: boolean;
}

const ProcessItems = styled.div`
  display: none;

  ${moreThan('md')} {
    display: flex;
    flex-wrap: wrap;
    margin: ${(props) => props.theme.margins.processItem};
  }
`;

const ProcessListItem = styled.div`
  padding: 0 ${calcrem(15)} ${calcrem(15)};
  width: 100%;
  height: 100%;

  ${moreThan('md')} {
    width: calc(100% / 2);
    padding: 10px;
  }

  ${moreThan('lg')} {
    width: calc(100% / 4);
  }
`;

const FactHeadline = styled(Headline)<{ isSubline?: boolean; centered?: boolean }>`
  margin-bottom: ${(props) => (props.isSubline ? '0px' : props.theme.margins.factHeadline)};
  text-align: ${(props) => !props.centered && props.theme.textAligns.factHeadline};
  font-size: ${(props) => props.theme.fontSizes.headline2};
  line-height: ${(props) => props.theme.lineHeights.headline2};
  font-family: ${(props) => props.theme.fonts.HeadlineBold};

  ${moreThan('md')} {
    font-size: ${(props) => props.theme.fontSizes.processHeadlineWideScreen};
    line-height: ${(props) => props.theme.lineHeights.headline2WideScreen};
  }
`;

const FactSubline = styled(Headline)`
  margin-bottom: ${(props) => props.theme.margins.factHeadline};
  text-align: ${(props) => !props.centered && props.theme.textAligns.factHeadline};
  font-size: ${(props) => props.theme.fontSizes.processSubline};
  line-height: ${(props) => props.theme.lineHeights.processSubline};

  ${moreThan('md')} {
    margin-bottom: ${(props) => props.theme.margins.factHeadlineWideScreen};
    font-size: ${(props) => props.theme.fontSizes.processSublineWideScreen};
    line-height: ${(props) => props.theme.lineHeights.processSublineWideScreen};
  }
`;

const ProcessCarousel = styled.div`
  display: block;
  margin: ${(props) => props.theme.margins.processCarousel};

  ${moreThan('md')} {
    display: none;
  }
`;

const ProcessComponent: React.FunctionComponent<{
  items: IProcessItem[];
  withNumbers?: boolean;
}> = ({ items, withNumbers }) => {
  const { isMobileScreen } = useisMobile();

  const processItems = items.map((entry, index) => (
    <ProcessListItem key={`jobFact-${index}`} data-value={index + 1} className="item">
      <ProcessItem {...entry} number={withNumbers ? index + 1 : undefined} />
    </ProcessListItem>
  ));

  return isMobileScreen ? (
    <ProcessCarousel>
      <Carousel items={processItems} />
    </ProcessCarousel>
  ) : (
    <ProcessItems>
      {items.map((entry, index) => (
        <ProcessListItem key={`jobFact-${index}`}>
          <ProcessItem {...entry} number={withNumbers ? index + 1 : undefined} />
        </ProcessListItem>
      ))}
    </ProcessItems>
  );
};

const Process: React.FunctionComponent<IProcess> = ({
  title,
  subline,
  items,
  centeredHeadline,
  withNumbers,
}) => {
  const isSubline = Boolean(subline);

  return (
    <Container>
      <FactHeadline tag="h2" centered={centeredHeadline} isSubline={isSubline}>
        {title}
      </FactHeadline>
      {subline && (
        <FactSubline centered={centeredHeadline} skin={2} tag="h3">
          {subline}
        </FactSubline>
      )}
      {items && <ProcessComponent items={[...items]} withNumbers={withNumbers} />}
    </Container>
  );
};

export default Process;
